// Page: ManageEnterprise
// Purpose: Admin detail page for a single enterprise. Loads enterprise data
// and users, provides assign/remove user actions, and displays plan stats.
import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { useAuth } from '../../../context/auth';
import { useToast } from '../../../components/ui/notifications/toast-notification.jsx';
import Footer from '../../../components/shared/Footer';
import { LoadingSpinner } from '../../../components/ui/feedback/loading-spinner.jsx';
import { PageHeader } from '../../../components/ui/layout/page-header';
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/layout/card';
import { ConfirmationModal } from '../../../components/ui/modals/confirmation-modal.jsx';
import { Building2, ArrowLeft, Users, Trash2, Edit3, UserX, Mail, Calendar, Crown, UserCheck } from 'lucide-react';
import { isUserAdmin } from '../../../utils/admin/adminUtils';
import api from '../../../api';

export default function ManageEnterprise() {
  const navigate = useNavigate();
  const { id } = useParams();
  const location = useLocation();
  const { user } = useAuth();
  const { addToast } = useToast();
  const [isLoading, setIsLoading] = useState(true);
  const [enterprise, setEnterprise] = useState(null);
  const [enterpriseUsers, setEnterpriseUsers] = useState([]);
  const [showDeleteUserModal, setShowDeleteUserModal] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);
  const [deletingUser, setDeletingUser] = useState(false);
  const [showAssignUserModal, setShowAssignUserModal] = useState(false);
  const [assigningUser, setAssigningUser] = useState(false);

  // Check if user is admin
  const isAdmin = isUserAdmin(user);

  useEffect(() => {
    if (user) {
      if (!isAdmin) {
        navigate('/app');
      } else {
        loadEnterpriseData();
      }
    } else if (user === null) {
      navigate('/login');
    }
  }, [navigate, user, isAdmin, id]);

  const loadEnterpriseData = async () => {
    try {
      // First try to use data passed via navigation state
      if (location.state?.enterprise) {
        setEnterprise(location.state.enterprise);
      }

      // Always fetch fresh data from API to get users
      const response = await api.get(`/admin/enterprises/${id}`);
      const enterpriseData = response.enterprise;
      
      if (enterpriseData) {
        setEnterprise(enterpriseData);
        
        // Fetch users for this enterprise using the correct dashboard endpoint
        await loadEnterpriseUsers(enterpriseData.enterpriseDomain);
      } else {
        addToast('Enterprise not found', 'error');
        navigate('/app/private/manageEnterprises');
      }
    } catch (err) {
      console.error('Error loading enterprise:', err);
      addToast('Failed to load enterprise data', 'error');
      navigate('/app/private/manageEnterprises');
    } finally {
      setIsLoading(false);
    }
  };

  const loadEnterpriseUsers = async (domain) => {
    try {
      // Use the correct dashboard endpoint that actually exists
      const response = await api.get('/admin/users/dashboard');
      const allUsers = response.users || [];
      
      console.log('=== ENTERPRISE USER DEBUGGING ===');
      console.log('All users from API:', allUsers.length);
      console.log('Filtering for domain:', domain);
      console.log('Enterprise ID we\'re looking for:', id);
      
      // Enhanced debugging: Show domain matches and enterprise flags separately
      const domainMatches = allUsers.filter(user => {
        const userEmail = user.email;
        return userEmail && userEmail.endsWith(`@${domain}`);
      });
      
      console.log(`\n--- USERS WITH DOMAIN @${domain} ---`);
      domainMatches.forEach((user, index) => {
        console.log(`${index + 1}. ${user.email}:`, {
          displayName: user.displayName,
          isEnterpriseUser: user.isEnterpriseUser,
          isEnterprise: user.isEnterprise,
          enterpriseId: user.enterpriseId,
          planLevel: user.planLevel,
          expectedEnterpriseId: id
        });
      });
      
      const enterpriseUsers = allUsers.filter(user => {
        return user.isEnterpriseUser === true || user.isEnterprise === true;
      });
      
      console.log(`\n--- ALL ENTERPRISE USERS (${enterpriseUsers.length}) ---`);
      enterpriseUsers.forEach((user, index) => {
        console.log(`${index + 1}. ${user.email}:`, {
          displayName: user.displayName,
          enterpriseId: user.enterpriseId,
          domain: user.email?.split('@')[1]
        });
      });
      
      // Filter users by BOTH enterprise domain AND actual enterprise user status
      const domainUsers = allUsers.filter(user => {
        const userEmail = user.email;
        const matchesDomain = userEmail && userEmail.endsWith(`@${domain}`);
        
        // CRITICAL: Only include users who are ACTUALLY marked as enterprise users
        // This prevents false positives for regular users with enterprise email domains
        const isActualEnterpriseUser = user.isEnterpriseUser === true || user.isEnterprise === true;
        
        const isValidEnterpriseUser = matchesDomain && isActualEnterpriseUser;
        
        console.log(`\nUser ${userEmail}:`, {
          matchesDomain,
          isEnterpriseUser: user.isEnterpriseUser,
          isEnterprise: user.isEnterprise,
          isActualEnterpriseUser,
          isValidEnterpriseUser,
          enterpriseId: user.enterpriseId,
          expectedEnterpriseId: id
        });
        
        return isValidEnterpriseUser;
      });
      
      console.log(`\n--- FINAL RESULTS ---`);
      console.log('Domain matches found:', domainMatches.length);
      console.log('Valid enterprise users found:', domainUsers.length);
      console.log('Valid enterprise users:', domainUsers.map(u => u.email));
      
      // DEBUGGING: For now, let's also check if there are domain matches without enterprise flags
      if (domainUsers.length === 0 && domainMatches.length > 0) {
        console.warn(`\n⚠️  PROBLEM IDENTIFIED:`);
        console.warn(`Found ${domainMatches.length} users with domain @${domain} but 0 are marked as enterprise users.`);
        console.warn('Non-enterprise users with this domain:', domainMatches.filter(u => 
          !(u.isEnterpriseUser === true || u.isEnterprise === true)
        ).map(u => ({
          email: u.email,
          displayName: u.displayName,
          isEnterpriseUser: u.isEnterpriseUser,
          isEnterprise: u.isEnterprise,
          planLevel: u.planLevel
        })));
        addToast(`Found ${domainMatches.length} users with domain @${domain} but they're not marked as enterprise users. Check enterprise assignment process.`, 'warning');
      }
      
      if (domainUsers.length === 0 && domainMatches.length === 0) {
        console.warn(`No users found with domain @${domain}. Enterprise may not have any users yet.`);
        addToast(`No users found with domain @${domain}. Users need to sign up with enterprise domain emails.`, 'info');
      }
      
      console.log('=== END DEBUGGING ===\n');
      
      setEnterpriseUsers(domainUsers);
    } catch (err) {
      console.error('Error loading enterprise users:', err);
      addToast('Failed to load enterprise users', 'error');
    }
  };

  const handleDeleteUser = async () => {
    if (!userToDelete) return;

    setDeletingUser(true);
    try {
      // Use the new admin users endpoint that uses proper admin authentication
      const response = await api.del(`/admin/users/${userToDelete.uid}`);
      
      // Show success message with deletion stats if available
      const stats = response.stats || {};
      const statsMessage = Object.entries(stats)
        .filter(([key, value]) => value > 0)
        .map(([key, value]) => `${value} ${key.replace(/([A-Z])/g, ' $1').toLowerCase()}`)
        .join(', ');
      
      const successMessage = statsMessage 
        ? `User ${userToDelete.email} deleted successfully along with ${statsMessage}`
        : `User ${userToDelete.email} and all associated data deleted successfully`;
      
      addToast(successMessage, 'success');
      
      // Remove from local state
      setEnterpriseUsers(prev => prev.filter(u => u.uid !== userToDelete.uid));
      
      setShowDeleteUserModal(false);
      setUserToDelete(null);
    } catch (err) {
      console.error('Error deleting user:', err);
      const errorMessage = err.message || 'Failed to delete user';
      addToast(errorMessage, 'error');
    } finally {
      setDeletingUser(false);
    }
  };

  const confirmDeleteUser = (user) => {
    setUserToDelete(user);
    setShowDeleteUserModal(true);
  };

  const handleAssignUser = async (userEmail) => {
    if (!userEmail) return;

    setAssigningUser(true);
    try {
      const response = await api.post(`/admin/enterprises/${enterprise.id}/assign-user`, {
        userEmail: userEmail
      });
      
      addToast(response.message || 'User assigned to enterprise successfully', 'success');
      
      // Reload enterprise users to reflect the change
      await loadEnterpriseUsers(enterprise.enterpriseDomain);
      
      setShowAssignUserModal(false);
    } catch (err) {
      console.error('Error assigning user to enterprise:', err);
      const errorMessage = err.message || 'Failed to assign user to enterprise';
      addToast(errorMessage, 'error');
    } finally {
      setAssigningUser(false);
    }
  };

  const handleEditEnterprise = () => {
    navigate(`/app/private/editEnterprise/${enterprise.id}`, {
      state: { enterprise }
    });
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    
    // Handle Firebase Timestamp objects
    if (dateString && typeof dateString === 'object' && dateString._seconds) {
      return new Date(dateString._seconds * 1000).toLocaleDateString();
    }
    
    // Handle regular date strings
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return 'N/A';
      return date.toLocaleDateString();
    } catch (error) {
      console.error('Error parsing date:', dateString, error);
      return 'N/A';
    }
  };

  const formatLastActive = (lastActive) => {
    if (!lastActive) return 'Never';
    
    const now = new Date();
    const lastActiveDate = new Date(lastActive);
    const diffMs = now - lastActiveDate;
    const diffMinutes = Math.floor(diffMs / (1000 * 60));
    
    if (diffMinutes < 10) {
      return 'Active now';
    } else if (diffMinutes < 60) {
      return `${diffMinutes} minutes ago`;
    } else if (diffMinutes < 1440) { // Less than 24 hours
      const hours = Math.floor(diffMinutes / 60);
      return `${hours} hour${hours > 1 ? 's' : ''} ago`;
    } else {
      const days = Math.floor(diffMinutes / 1440);
      return `${days} day${days > 1 ? 's' : ''} ago`;
    }
  };

  const getPlanLevelBadge = (planLevel) => {
    // For enterprise users, show the enterprise plan level instead of individual plan level
    const enterprisePlanLevel = enterprise?.planType || 'premium'; // Use enterprise plan type
    
    const colors = {
      basic: 'bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-400',
      premium: 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-400'
    };
    
    return (
      <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${colors[enterprisePlanLevel] || colors.premium}`}>
        Enterprise {enterprisePlanLevel}
      </span>
    );
  };

  // Calculate summary stats based on enterprise plan level (all users should be enterprise level)
  const summaryStats = {
    totalUsers: enterpriseUsers.length,
    // All enterprise users should be treated as having the enterprise plan level
    premiumUsers: enterprise?.planType === 'premium' ? enterpriseUsers.length : 0,
    basicUsers: 0 // Enterprise users are never basic
  };

  if (isLoading) {
    return (
      <div className="p-6 flex items-center justify-center h-screen">
        <LoadingSpinner message="Loading enterprise data..." />
      </div>
    );
  }

  if (!isAdmin || !enterprise) {
    return null;
  }

  return (
    <div className="p-6 max-w-[1600px] mx-auto pb-20">
      {/* Back to Manage Enterprises button */}
      <div className="mb-4">
        <button
          onClick={() => navigate('/app/private/manageEnterprises')}
          className="inline-flex items-center text-sm font-medium text-gray-500 hover:text-gray-900 dark:text-gray-400 dark:hover:text-white"
        >
          <ArrowLeft className="w-4 h-4 mr-1.5" />
          Back to Manage Enterprises
        </button>
      </div>

      <PageHeader
        title={`Manage ${enterprise.enterpriseName}`}
        description={`Manage users and settings for ${enterprise.enterpriseName} enterprise account`}
      >
        <button
          onClick={handleEditEnterprise}
          className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-md hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
        >
          <Edit3 className="h-4 w-4" />
          Edit Enterprise
        </button>
      </PageHeader>

      {/* Enterprise Overview */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-8">
        {/* Enterprise Info Card */}
        <Card className="lg:col-span-2">
          <CardContent className="space-y-4 pt-6">
            <div className="flex items-center gap-4">
              {(enterprise.enterpriseImageUrl || enterprise.enterpriseImage) ? (
                <img
                  src={enterprise.enterpriseImageUrl || enterprise.enterpriseImage}
                  alt={`${enterprise.enterpriseName} logo`}
                  className="h-16 w-16 object-contain rounded-lg border border-gray-200 dark:border-gray-700"
                />
              ) : (
                <div className="h-16 w-16 bg-gray-100 dark:bg-gray-800 rounded-lg flex items-center justify-center border border-gray-200 dark:border-gray-700">
                  <Building2 className="h-8 w-8 text-gray-400" />
                </div>
              )}
              <div>
                <h3 className="text-xl font-semibold text-gray-900 dark:text-white">
                  {enterprise.enterpriseName}
                </h3>
                <p className="text-gray-500 dark:text-gray-400">
                  {enterprise.enterpriseDomain}
                </p>
              </div>
            </div>

            <div className="grid grid-cols-2 lg:grid-cols-3 gap-6 pt-4 border-t border-gray-200 dark:border-gray-700">
              <div>
                <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Plan Type</p>
                <p className="text-lg font-semibold text-gray-900 dark:text-white capitalize">
                  {enterprise.planType} • {enterprise.planBilling}
                </p>
              </div>
              <div>
                <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Max Accounts</p>
                <p className="text-lg font-semibold text-gray-900 dark:text-white">
                  {enterprise.maxAccounts || 'Unlimited'}
                </p>
              </div>
              <div>
                <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Current Users</p>
                <p className="text-lg font-semibold text-gray-900 dark:text-white">
                  {summaryStats.totalUsers}
                </p>
              </div>
              <div>
                <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Created</p>
                <p className="text-lg font-semibold text-gray-900 dark:text-white">
                  {formatDate(enterprise.createdAt)}
                </p>
              </div>
              {enterprise.adminEmail && (
                <div>
                  <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Admin Email</p>
                  <p className="text-lg font-semibold text-gray-900 dark:text-white">
                    {enterprise.adminEmail}
                  </p>
                </div>
              )}
              {/* Removed Custom Templates and AI Tools access control displays */}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* User Statistics */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-8">
        <div className="bg-white dark:bg-transparent border border-gray-200 dark:border-gray-700 rounded-lg p-4">
          <div className="flex items-center">
            <Users className="h-5 w-5 text-gray-500 mr-2" />
            <div>
              <p className="text-sm text-gray-600 dark:text-gray-400">Total Users</p>
              <p className="text-2xl font-semibold text-gray-900 dark:text-white">{summaryStats.totalUsers}</p>
            </div>
          </div>
        </div>

        <div className="bg-white dark:bg-transparent border border-gray-200 dark:border-gray-700 rounded-lg p-4">
          <div className="flex items-center">
            <Crown className="h-5 w-5 text-purple-500 mr-2" />
            <div>
              <p className="text-sm text-gray-600 dark:text-gray-400">Enterprise {enterprise?.planType || 'Premium'} Users</p>
              <p className="text-2xl font-semibold text-gray-900 dark:text-white">{summaryStats.totalUsers}</p>
            </div>
          </div>
        </div>

        <div className="bg-white dark:bg-transparent border border-gray-200 dark:border-gray-700 rounded-lg p-4">
          <div className="flex items-center">
            <Calendar className="h-5 w-5 text-green-500 mr-2" />
            <div>
              <p className="text-sm text-gray-600 dark:text-gray-400">Active Users</p>
              <p className="text-2xl font-semibold text-gray-900 dark:text-white">
                {enterpriseUsers.filter(u => u.lastActive && new Date(u.lastActive) > new Date(Date.now() - 7 * 24 * 60 * 60 * 1000)).length}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white dark:bg-transparent border border-gray-200 dark:border-gray-700 rounded-lg p-4">
          <div className="flex items-center">
            <Mail className="h-5 w-5 text-amber-500 mr-2" />
            <div>
              <p className="text-sm text-gray-600 dark:text-gray-400">Verified Users</p>
              <p className="text-2xl font-semibold text-gray-900 dark:text-white">
                {enterpriseUsers.filter(u => u.isEmailVerified).length}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Users Table */}
      <div className="bg-white dark:bg-transparent border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden mt-8">
        <div className="border-b border-gray-200 dark:border-gray-700 px-6 py-4">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-lg font-medium text-gray-900 dark:text-white">Enterprise Users ({enterpriseUsers.length})</h2>
              <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
                Users with @{enterprise.enterpriseDomain} email addresses. All enterprise users inherit the {enterprise.planType} plan level.
              </p>
            </div>
            <button
              onClick={() => setShowAssignUserModal(true)}
              className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              <UserCheck className="h-4 w-4" />
              Assign User
            </button>
          </div>
        </div>
        
        <div className="overflow-x-auto">
          {enterpriseUsers.length === 0 ? (
            <div className="text-center py-12">
              <Users className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
                No users yet
              </h3>
              <p className="text-gray-500 dark:text-gray-400">
                Users with @{enterprise.enterpriseDomain} email addresses will appear here once they sign up.
              </p>
            </div>
          ) : (
            <table className="w-full">
              <thead className="bg-gray-50 dark:bg-gray-800/50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Name
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Email
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Enterprise Plan
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Member Since
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Last Active
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white dark:bg-transparent divide-y divide-gray-200 dark:divide-gray-700">
                {enterpriseUsers.map((enterpriseUser) => (
                  <tr key={enterpriseUser.uid} className="hover:bg-gray-50 dark:hover:bg-gray-800/50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="h-8 w-8 bg-gray-100 dark:bg-gray-800 rounded-full flex items-center justify-center mr-3">
                          <span className="text-xs font-medium text-gray-600 dark:text-gray-300">
                            {enterpriseUser.displayName?.[0]?.toUpperCase() || enterpriseUser.email?.[0]?.toUpperCase() || 'U'}
                          </span>
                        </div>
                        <div>
                          <div className="text-sm font-medium text-gray-900 dark:text-white">
                            {enterpriseUser.displayName || 'Name not set'}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center text-sm text-gray-500 dark:text-gray-400">
                        <Mail className="h-3 w-3 mr-1" />
                        {enterpriseUser.email}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {getPlanLevelBadge(enterpriseUser.planLevel)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center text-xs text-gray-500 dark:text-gray-400">
                        <Calendar className="h-3 w-3 mr-1" />
                        {formatDate(enterpriseUser.createdAt)}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-xs text-gray-500 dark:text-gray-400">
                      {formatLastActive(enterpriseUser.lastActive)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <button
                        onClick={() => confirmDeleteUser(enterpriseUser)}
                        className="flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-red-700 bg-red-50 border border-red-200 rounded-md hover:bg-red-100 dark:bg-red-900/20 dark:text-red-300 dark:border-red-700/50 dark:hover:bg-red-900/30"
                      >
                        <UserX className="h-3 w-3" />
                        Remove
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {/* Delete User Confirmation Modal */}
      <ConfirmationModal
        isOpen={showDeleteUserModal}
        onClose={() => {
          setShowDeleteUserModal(false);
          setUserToDelete(null);
        }}
        onConfirm={handleDeleteUser}
        title="Remove User from Enterprise"
        message={
          <>
            <span>Are you sure you want to remove <strong>{userToDelete?.email}</strong> from this enterprise?</span>
            <br />
            <span className="text-sm text-red-600 dark:text-red-400">
              This will permanently delete their account and all associated data. This action cannot be undone.
            </span>
          </>
        }
        confirmButtonText="Remove User"
        isLoading={deletingUser}
      />

      {/* Assign User Modal */}
      {showAssignUserModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 w-full max-w-md">
            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">
              Assign User to {enterprise.enterpriseName}
            </h3>
            <form onSubmit={(e) => {
              e.preventDefault();
              const formData = new FormData(e.target);
              const userEmail = formData.get('userEmail');
              handleAssignUser(userEmail);
            }}>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  User Email Address
                </label>
                <input
                  type="email"
                  name="userEmail"
                  required
                  placeholder={`user@${enterprise.enterpriseDomain}`}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                />
                <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                  Email must be from @{enterprise.enterpriseDomain} domain and the user must already have an account.
                </p>
              </div>
              <div className="flex gap-3">
                <button
                  type="submit"
                  disabled={assigningUser}
                  className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {assigningUser ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                      Assigning...
                    </>
                  ) : (
                    <>
                      <UserCheck className="h-4 w-4" />
                      Assign User
                    </>
                  )}
                </button>
                <button
                  type="button"
                  onClick={() => setShowAssignUserModal(false)}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 dark:bg-gray-700 dark:text-gray-300 dark:border-gray-600 dark:hover:bg-gray-600"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
} 